import { useMemo, useState, useRef, useEffect } from 'react';
import { Popover } from '@mui/material';
import { t } from 'utilities/TranslationUtils';
import {
  MeasurementMarkerDto,
  SessionOptionDto,
  formatKindLabel,
  SessionKindGroupName,
} from 'types/models/SessionsPageData';
import { getMeasurementFieldValue, MeasurementNumericField } from 'pages/PlayerPerformance/playerPerformanceUtils';
import { getAvailableMetricsForKind, getDefaultColumnsForKind, type ColumnMetricKey as ConfigColumnMetricKey } from '../config/measurementTableMetricsConfig';
import KickResultChip from './KickResultChip';

type Props = {
  data: MeasurementMarkerDto[];
  sessions: SessionOptionDto[];
  selectedIds: Set<string>;
  onToggleKick: (id: string) => void;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  showSelectionControls?: boolean;
  performanceMode?: boolean;
  activeMeasurementId?: string | null;
  onSelectMeasurement?: (id: string) => void;
  onShowVideosForMeasurement?: (id: string) => void;
  onFilterSessionOnly?: (sessionId: string) => void;
  onAddSessionMeasurements?: (sessionId: string, measurementIds: string[]) => void;
  onMoreInfo?: (m: MeasurementMarkerDto) => void;
  kind?: SessionKindGroupName;
  initialComparisonId?: string | null;
};

/**
 * Tabular view of all kicks currently visible (after period+kind filtering).
 * Sorted by session captured-time desc, then kick time desc, so the freshest sessions appear first
 * and within each session the latest kicks lead.
 *
 * Each row exposes:
 *   - Kick ID (truncated, click to copy)
 *   - Session ID (truncated, click to copy)
 *   - Session type label
 *   - Player name
 *   - Kick captured-at
 *   - Toggle button (Add → Remove) whose state mirrors `selectedIds`
 */
type MetricType =
  | 'ballSpeed'
  | 'hangTime'
  | 'kickX'
  | 'kickZ'
  | 'goalY'
  | 'goalZ'
  | 'landingX'
  | 'landingZ'
  | 'videoCount';

const METRIC_OPTIONS: { value: MetricType; labelKey: string; unitKey: string; accessor: (m: MeasurementMarkerDto) => number | null }[] = [
  { value: 'ballSpeed', labelKey: 'sessions.measureMetricBallSpeed', unitKey: 'common.unitKmh', accessor: (m) => m.ballSpeed },
  { value: 'hangTime', labelKey: 'sessions.measureMetricHangTime', unitKey: 'common.unitSeconds', accessor: (m) => m.hangTime },
  { value: 'kickX', labelKey: 'sessions.measureMetricKickX', unitKey: 'common.unitMeters', accessor: (m) => m.kickX },
  { value: 'kickZ', labelKey: 'sessions.measureMetricKickZ', unitKey: 'common.unitMeters', accessor: (m) => m.kickZ },
  { value: 'goalY', labelKey: 'sessions.measureMetricGoalY', unitKey: 'common.unitMeters', accessor: (m) => m.goalY },
  { value: 'goalZ', labelKey: 'sessions.measureMetricGoalZ', unitKey: 'common.unitMeters', accessor: (m) => m.goalZ },
  { value: 'landingX', labelKey: 'sessions.measureMetricLandingX', unitKey: 'common.unitMeters', accessor: (m) => m.landingX },
  { value: 'landingZ', labelKey: 'sessions.measureMetricLandingZ', unitKey: 'common.unitMeters', accessor: (m) => m.landingZ },
  { value: 'videoCount', labelKey: 'sessions.measureMetricVideos', unitKey: '', accessor: (m) => m.videoCount },
];

type ColumnMetricKey = MeasurementNumericField;

/** Builds the column-picker labels, resolving unit suffixes from the shared `common.unit*` keys. */
function buildColumnMetricLabels(): Record<ColumnMetricKey, string> {
  return {
    ballSpeed: t('sessions.measureColSpeed', { unit: t('common.unitKmh') }),
    hangTime: t('sessions.measureColHang', { unit: t('common.unitSeconds') }),
    distance: t('sessions.measureColDistance', { unit: t('common.unitMeters') }),
    gFactor: t('sessions.measureColGForce'),
    spinRate: t('sessions.measureColSpin', { unit: t('common.unitRpm') }),
    spinAxis: t('sessions.measureColSpinAxis', { unit: t('common.unitDegrees') }),
    launchAngle: t('sessions.measureColLaunchAngle', { unit: t('common.unitDegrees') }),
    launchDirection: t('sessions.measureColLaunchDir', { unit: t('common.unitDegrees') }),
    pullDirection: t('sessions.measureColPullDir', { unit: t('common.unitDegrees') }),
    maxHeight: t('sessions.measureColMaxHeight', { unit: t('common.unitMeters') }),
    goalCrossingSpeed: t('sessions.measureColGoalSpeed', { unit: t('common.unitKmh') }),
    wallCrossingHeight: t('sessions.measureColWallHeight', { unit: t('common.unitMeters') }),
  };
}

const getMeasurementMetricValue = getMeasurementFieldValue;

function formatColumnValue(value: number | null, key: ColumnMetricKey): string {
  if (value == null) return '—';
  switch (key) {
    case 'ballSpeed': case 'goalCrossingSpeed': return `${value.toFixed(1)} ${t('common.unitKmh')}`;
    case 'hangTime': return `${value.toFixed(2)}${t('common.unitSeconds')}`;
    case 'distance': case 'maxHeight': case 'wallCrossingHeight': return `${value.toFixed(1)}${t('common.unitMeters')}`;
    case 'gFactor': return `${value.toFixed(2)}${t('common.unitGForce')}`;
    case 'spinRate': return `${value.toFixed(0)} ${t('common.unitRpm')}`;
    case 'spinAxis': case 'launchAngle': case 'launchDirection': case 'pullDirection': return `${value.toFixed(1)}${t('common.unitDegrees')}`;
  }
}

const MeasurementsTable = ({
  data,
  sessions,
  selectedIds,
  onToggleKick,
  hasActiveFilters,
  onClearFilters,
  showSelectionControls = true,
  performanceMode = false,
  activeMeasurementId = null,
  onSelectMeasurement,
  onShowVideosForMeasurement,
  onFilterSessionOnly,
  onAddSessionMeasurements,
  onMoreInfo,
  kind,
  initialComparisonId,
}: Props): React.ReactElement => {
  const [sessionAnchorEl, setSessionAnchorEl] = useState<HTMLElement | null>(null);
  const [sessionPopoverId, setSessionPopoverId] = useState<string | null>(null);
  const [metricAnchorEl, setMetricAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('ballSpeed');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const tableRef = useRef<HTMLTableElement>(null);
  useEffect(() => {
    if (!initialComparisonId) return;
    const timer = setTimeout(() => {
      tableRef.current
        ?.querySelector(`[data-measurement-id="${initialComparisonId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    return () => clearTimeout(timer);
  }, [initialComparisonId]);
  
  // Initialize columns from kind-specific defaults
  const [defaultColumns] = useState(() => getDefaultColumnsForKind(kind));
  const [col0, setCol0] = useState<ColumnMetricKey>(defaultColumns[0]);
  const [col1, setCol1] = useState<ColumnMetricKey>(defaultColumns[1]);
  const [col2, setCol2] = useState<ColumnMetricKey>(defaultColumns[2]);
  
  // Reset columns when kind changes
  useEffect(() => {
    const [newCol0, newCol1, newCol2] = getDefaultColumnsForKind(kind);
    setCol0(newCol0);
    setCol1(newCol1);
    setCol2(newCol2);
  }, [kind]);
  const columns = [
    { key: col0, set: setCol0 },
    { key: col1, set: setCol1 },
    { key: col2, set: setCol2 },
  ] as const;

  const isPerformanceTable = !!onMoreInfo;

  const measurementIdsBySession = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const m of data) {
      const arr = map.get(m.sessionId) ?? [];
      arr.push(m.id);
      map.set(m.sessionId, arr);
    }
    return map;
  }, [data]);

  const closeSessionPopover = () => {
    setSessionAnchorEl(null);
    setSessionPopoverId(null);
  };

  const closeMetricPopover = () => {
    setMetricAnchorEl(null);
  };

  const metricOption = METRIC_OPTIONS.find((o) => o.value === selectedMetric) || METRIC_OPTIONS[0];
  const metricOptionLabel = t(metricOption.labelKey);
  const columnMetricLabels = useMemo(() => buildColumnMetricLabels(), []);

  const renderMetricValue = (measurement: MeasurementMarkerDto): string => {
    const value = metricOption.accessor(measurement);
    if (value === null) return '—';
    return metricOption.unitKey ? `${value} ${t(metricOption.unitKey)}` : `${value}`;
  };

  const sessionStartById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of sessions) map.set(s.id, s.capturedAtUtc);
    return map;
  }, [sessions]);

  const sorted = useMemo(() => {
    let result = [...data];
    if (isPerformanceTable) {
      // Performance table always shows latest kicks first
      result.sort((a, b) => b.capturedAtUtc.localeCompare(a.capturedAtUtc));
    } else {
      // Sessions table: sort by selected metric when values exist, fall back to session time
      const metricAccessor = metricOption.accessor;
      const hasMetricValues = result.some((m) => metricAccessor(m) !== null);
      if (hasMetricValues) {
        result.sort((a, b) => {
          const aVal = metricAccessor(a) ?? -Infinity;
          const bVal = metricAccessor(b) ?? -Infinity;
          const cmp = aVal - bVal;
          return sortDirection === 'desc' ? -cmp : cmp;
        });
      } else {
        result.sort((a, b) => {
          const sa = sessionStartById.get(a.sessionId) ?? a.capturedAtUtc;
          const sb = sessionStartById.get(b.sessionId) ?? b.capturedAtUtc;
          if (sa !== sb) return sb.localeCompare(sa);
          return b.capturedAtUtc.localeCompare(a.capturedAtUtc);
        });
      }
    }
    return result;
  }, [data, sessionStartById, isPerformanceTable, selectedMetric, sortDirection, metricOption]);

  const showExcludeAction = performanceMode;
  const showActionColumn = showSelectionControls || showExcludeAction;
  const comparisonId = useMemo(() => {
    if (!isPerformanceTable || selectedIds.size !== 1) return null;
    const [id] = Array.from(selectedIds);
    return sorted.some((m) => m.id === id) ? id : null;
  }, [isPerformanceTable, selectedIds, sorted]);

  const renderVideoCell = (measurement: MeasurementMarkerDto): React.ReactNode => {
    const count = measurement.videoCount;
    if (count === null || count === undefined) return '—';
    const label = count === 1
      ? t('sessions.analysisKickVideosOne')
      : t('sessions.analysisKickVideosMany', { count });

    if (!performanceMode || !onShowVideosForMeasurement || count <= 0) return label;

    return (
      <button
        type='button'
        className='sessions-analysis-videos-btn'
        onClick={(event) => {
          event.stopPropagation();
          onShowVideosForMeasurement(measurement.id);
        }}
      >
        {label}
      </button>
    );
  };

  const copy = async (text: string) => {
    try { await navigator.clipboard.writeText(text); } catch { /* ignore */ }
  };

  return (
    <div className='sessions-measurements-table-wrapper'>
      <table ref={tableRef} className='sessions-measurements-table'>
        <thead>
          <tr>
            {isPerformanceTable ? (
              <>
                <th style={{ width: 28 }} aria-label={t('sessions.tableFavoriteAriaLabel')} />
                <th>{t('sessions.tableColKickTime')}</th>
                <th>{t('sessions.tableColResult')}</th>
                {columns.map((col, i) => (
                  <th key={i} className='meas-table-col-picker-header'>
                    <select
                      className='meas-table-col-picker'
                      value={col.key}
                      onChange={e => col.set(e.target.value as ColumnMetricKey)}
                    >
                      {getAvailableMetricsForKind(kind).map(k => (
                        <option key={k} value={k}>{columnMetricLabels[k]}</option>
                      ))}
                    </select>
                  </th>
                ))}
                {showSelectionControls && <th />}
                <th style={{ width: 32 }} />
              </>
            ) : (
              <>
                <th>{t('sessions.tableColPlayer')}</th>
                <th>{t('sessions.tableColSessionType')}</th>
                <th>{t('sessions.tableColKickTime')}</th>
                <th>{t('sessions.tableColResult')}</th>
                {performanceMode && <th>{t('sessions.analysisKickColVideos')}</th>}
                <th>
                  <button
                    type='button'
                    className='sessions-table-metric-header'
                    onClick={(e) => setMetricAnchorEl(e.currentTarget)}
                    title={t('sessions.measureChangeMetricTitle', { metric: metricOptionLabel })}
                  >
                    <span>{metricOptionLabel}</span>
                    <span className='sessions-table-metric-sort'>
                      {sortDirection === 'desc' ? '↓' : '↑'}
                    </span>
                  </button>
                </th>
                <th>{t('sessions.tableColKickId')}</th>
                <th>{t('sessions.tableColSessionId')}</th>
                {showActionColumn && <th />}
              </>
            )}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 ? (
            <tr>
              <td colSpan={1000}>
                <div className='sessions-empty-state'>
                  <p className='chart-empty'>{t('sessions.chartEmpty')}</p>
                  {hasActiveFilters && onClearFilters && (
                    <button
                      type='button'
                      className='sessions-reset-btn'
                      onClick={onClearFilters}
                    >
                      {t('sessions.clearFiltersEmptyState')}
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ) : (() => {
            const compMeasurement = comparisonId ? sorted.find((x) => x.id === comparisonId) ?? null : null;
            return sorted.map((m) => {
            const selected = selectedIds.has(m.id);
            const isActive = activeMeasurementId === m.id;
            const isComparison = m.id === comparisonId;
            const rowClassName = [
              isPerformanceTable ? 'meas-table-row--clickable' : '',
              isComparison ? 'sessions-measurements-table-row--comparison' : '',
              selected ? 'sessions-measurements-table-row--selected' : '',
              isActive ? 'sessions-measurements-table-row--active' : '',
            ].filter(Boolean).join(' ');
            return (
              <tr
                key={m.id}
                data-measurement-id={m.id}
                className={rowClassName}
                onClick={() => {
                  if (isPerformanceTable) {
                    // Row click opens detail modal with 3D/video, not selection toggle.
                    // Selection toggle is via the dedicated toggle button column.
                    onMoreInfo?.(m);
                    return;
                  }
                  if (performanceMode && onSelectMeasurement) {
                    onSelectMeasurement(m.id);
                    return;
                  }
                  if (!showSelectionControls) return;
                  onToggleKick(m.id);
                }}
              >
                {isPerformanceTable ? (
                  <>
                    <td
                      className={`meas-table-star${m.isFavorite ? ' meas-table-star--filled' : ''}`}
                      aria-label={m.isFavorite ? t('sessions.tableFavoriteAriaLabel') : undefined}
                    >
                      {m.isFavorite ? '★' : '☆'}
                    </td>
                    <td title={new Date(m.capturedAtUtc).toLocaleString(undefined, { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}>
                      {new Date(m.capturedAtUtc).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td>{m.result ? <KickResultChip result={m.result} kind={kind} onTarget={m.onTarget ?? null} /> : '—'}</td>
                    {columns.map((col, ci) => {
                      const value = getMeasurementMetricValue(m, col.key);
                      const compValue = compMeasurement ? getMeasurementMetricValue(compMeasurement, col.key) : null;
                      const delta = compMeasurement && value != null && compValue != null ? value - compValue : null;
                      return (
                        <td key={ci} className='meas-table-metric'>
                          <div>{formatColumnValue(value, col.key)}</div>
                          {delta != null && !isComparison && (
                            <div className={`meas-table-delta meas-table-delta--${delta >= 0 ? 'pos' : 'neg'}`}>
                              {delta >= 0 ? '+' : ''}{formatColumnValue(delta, col.key)}
                            </div>
                          )}
                        </td>
                      );
                    })}
                    {showSelectionControls && (
                      <td>
                        <button
                          type='button'
                          className={`sessions-measurements-table-toggle${selected ? ' sessions-measurements-table-toggle--remove' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleKick(m.id);
                          }}
                          aria-pressed={selected}
                          aria-label={selected ? t('sessions.tableRemove') : t('sessions.tableAdd')}
                          title={selected ? t('sessions.tableRemove') : t('sessions.tableAdd')}
                        >
                          {selected ? '×' : t('sessions.tableAdd')}
                        </button>
                      </td>
                    )}
                    <td className='meas-table-chevron'>
                      <button
                        type='button'
                        className='meas-table-open-detail'
                        aria-label={t('sessions.kickOpenMeasurements')}
                        title={t('sessions.kickOpenMeasurements')}
                        onClick={(e) => { e.stopPropagation(); onMoreInfo?.(m); }}
                      >
                        ›
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{m.playerName}</td>
                    <td>{formatKindLabel(m.kind as SessionKindGroupName)}</td>
                    <td title={new Date(m.capturedAtUtc).toLocaleString(undefined, { weekday: 'short', month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}>
                      {new Date(m.capturedAtUtc).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </td>
                    <td>{m.result ? <KickResultChip result={m.result} kind={m.kind} onTarget={m.onTarget ?? null} /> : '—'}</td>
                    {performanceMode && <td>{renderVideoCell(m)}</td>}
                    <td className='sessions-table-metric-cell'>
                      {renderMetricValue(m)}
                    </td>
                    <td>
                      <button type='button' className='sessions-measurements-table-id' onClick={(e) => { e.stopPropagation(); copy(m.id); }} title={m.id}>
                        {m.id.slice(0, 8)}…
                      </button>
                    </td>
                    <td>
                      <button
                        type='button'
                        className='sessions-measurements-table-id'
                        onClick={(e) => {
                          e.stopPropagation();
                          if (performanceMode) {
                            copy(m.sessionId);
                            return;
                          }
                          setSessionAnchorEl(e.currentTarget);
                          setSessionPopoverId(m.sessionId);
                        }}
                        title={m.sessionId}
                      >
                        {m.sessionId.slice(0, 8)}…
                      </button>
                    </td>
                    {showSelectionControls && (
                      <td>
                        <button
                          type='button'
                          className={`sessions-measurements-table-toggle${selected ? ' sessions-measurements-table-toggle--remove' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleKick(m.id);
                          }}
                          aria-pressed={selected}
                        >
                          {selected ? t('sessions.tableRemove') : t('sessions.tableAdd')}
                        </button>
                      </td>
                    )}
                    {showExcludeAction && (
                      <td>
                        <button
                          type='button'
                          className='sessions-measurements-table-toggle sessions-measurements-table-toggle--icon sessions-measurements-table-toggle--neutral'
                          onClick={(e) => {
                            e.stopPropagation();
                            onToggleKick(m.id);
                          }}
                          aria-label={t('sessions.measureExcludeFromAnalysis')}
                          title={t('sessions.measureExcludeFromAnalysis')}
                        >
                          ×
                        </button>
                      </td>
                    )}
                  </>
                )}
              </tr>
            );
          });
          })()}
        </tbody>
      </table>

      <Popover
        open={!performanceMode && Boolean(sessionAnchorEl) && Boolean(sessionPopoverId)}
        anchorEl={sessionAnchorEl}
        onClose={closeSessionPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1, minWidth: 280 } }}
      >
        <div className='sessions-session-popover-actions'>
          <button
            type='button'
            className='sessions-popover-add'
            onClick={() => {
              if (sessionPopoverId && onFilterSessionOnly) onFilterSessionOnly(sessionPopoverId);
              closeSessionPopover();
            }}
          >
            {t('sessions.tableSessionActionFilterOnly')}
          </button>
          <button
            type='button'
            className='sessions-popover-add sessions-popover-add--primary'
            onClick={() => {
              if (sessionPopoverId && onAddSessionMeasurements) {
                onAddSessionMeasurements(sessionPopoverId, measurementIdsBySession.get(sessionPopoverId) ?? []);
              }
              closeSessionPopover();
            }}
          >
            {t('sessions.tableSessionActionAddMeasurements')}
          </button>
        </div>
      </Popover>

      <Popover
        open={Boolean(metricAnchorEl)}
        anchorEl={metricAnchorEl}
        onClose={closeMetricPopover}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{ sx: { p: 1, minWidth: 240 } }}
      >
        <div className='sessions-metric-picker'>
          <p className='sessions-metric-picker-label'>{t('sessions.measureMetricPickerLabel')}</p>
          {METRIC_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type='button'
              className={`sessions-metric-picker-option${selectedMetric === opt.value ? ' sessions-metric-picker-option--active' : ''}`}
              onClick={() => {
                setSelectedMetric(opt.value);
                setSortDirection('desc');
                closeMetricPopover();
              }}
            >
              {t(opt.labelKey)}
            </button>
          ))}
          <div className='sessions-metric-picker-divider' />
          <p className='sessions-metric-picker-label'>{t('sessions.measureSortLabel')}</p>
          <button
            type='button'
            className={`sessions-metric-picker-option${sortDirection === 'desc' ? ' sessions-metric-picker-option--active' : ''}`}
            onClick={() => {
              setSortDirection('desc');
              closeMetricPopover();
            }}
          >
            {t('sessions.measureSortHighest')}
          </button>
          <button
            type='button'
            className={`sessions-metric-picker-option${sortDirection === 'asc' ? ' sessions-metric-picker-option--active' : ''}`}
            onClick={() => {
              setSortDirection('asc');
              closeMetricPopover();
            }}
          >
            {t('sessions.measureSortLowest')}
          </button>
        </div>
      </Popover>
    </div>
  );
};

export default MeasurementsTable;
